import { useState, useEffect } from 'react';
import { Users, FolderOpen, Activity, DollarSign, Database, FileText, Globe, Mail } from 'lucide-react';
import BankAccountsBalance from '@/components/BankAccountsBalance';
import BankAccountMovements from '@/components/BankAccountMovements';
import PendingDebitsMonitor from '@/components/PendingDebitsMonitor';
import AdvancedAnalytics from '@/components/AdvancedAnalytics';
import BankCommissionsTable from '@/components/admin/BankCommissionsTable';
import DIUnsettledAlert from '@/components/admin/DIUnsettledAlert';
import DataHealthCard from '@/components/admin/DataHealthCard';
import DIReviewQueue from '@/components/admin/DIReviewQueue';
import UnmatchedCreditsQueue from '@/components/admin/UnmatchedCreditsQueue';
import ErrorBoundary from '@/components/ErrorBoundary';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardTab({ stats, serverInfo, exchangeRate, setExchangeRate, setToast, onReload }) {
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [togglingEmail, setTogglingEmail] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/super-admin/email-settings`)
      .then(res => setEmailEnabled(res.data.onboarding_email_enabled))
      .catch(() => {});
  }, []);

  const toggleEmail = async () => {
    setTogglingEmail(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/super-admin/email-settings?enabled=${!emailEnabled}`, null);
      setEmailEnabled(res.data.onboarding_email_enabled);
      setToast({ type: 'success', message: res.data.message });
    } catch (e) { setToast({ type: 'error', message: 'Error al cambiar configuracion de email' }); }
    finally { setTogglingEmail(false); }
  };

  const refreshRate = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/exchange-rate?force=true`);
      setExchangeRate(res.data);
      setToast({ type: 'success', message: 'Tasa actualizada' });
    } catch (e) { setToast({ type: 'error', message: 'Error al actualizar tasa' }); }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#051C2C' }}>Estadisticas Globales</h2>
      <DIUnsettledAlert />
      <DIReviewQueue setToast={setToast} />
      <UnmatchedCreditsQueue setToast={setToast} />
      <DataHealthCard setToast={setToast} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 border">
          <Users className="w-8 h-8 mb-3" style={{ color: '#003594' }} />
          <h3 className="text-sm text-gray-600">Comercios</h3>
          <p className="text-3xl font-bold mt-1">{stats.total_merchants}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <FolderOpen className="w-8 h-8 mb-3" style={{ color: '#008AD8' }} />
          <h3 className="text-sm text-gray-600">Proyectos</h3>
          <p className="text-3xl font-bold mt-1">{stats.total_projects}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <Activity className="w-8 h-8 mb-3 text-green-600" />
          <h3 className="text-sm text-gray-600">Transacciones</h3>
          <p className="text-3xl font-bold mt-1 text-green-600">{stats.total_transactions}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <DollarSign className="w-8 h-8 mb-3 text-orange-600" />
          <h3 className="text-sm text-gray-600">Volumen</h3>
          <p className="text-3xl font-bold mt-1 text-orange-600">VES {(stats.total_volume || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <Database className="w-8 h-8 mb-3" style={{ color: '#003594' }} />
          <h3 className="text-sm text-gray-600">Wallets</h3>
          <p className="text-3xl font-bold mt-1">{stats.total_wallets}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <FileText className="w-8 h-8 mb-3 text-yellow-600" />
          <h3 className="text-sm text-gray-600">Ordenes Activas</h3>
          <p className="text-3xl font-bold mt-1 text-yellow-600">{stats.active_orders}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <Activity className="w-8 h-8 mb-3" style={{ color: '#008AD8' }} />
          <h3 className="text-sm text-gray-600">Dispersiones</h3>
          <p className="text-3xl font-bold mt-1">{stats.total_dispersions}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border">
          <DollarSign className="w-8 h-8 mb-3 text-green-600" />
          <h3 className="text-sm text-gray-600">Comisiones</h3>
          <p className="text-3xl font-bold mt-1 text-green-600">VES {(stats.total_fees_collected || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Server IP */}
      {serverInfo && (
        <div data-testid="server-ip-panel" className="mb-6 bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5" style={{ color: '#003594' }} />
              <div>
                <h3 className="text-sm font-semibold text-gray-800">IP del Servidor</h3>
                <p className="text-xs text-gray-500">Para whitelist de Banco Plaza</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <code data-testid="server-ip-value" className={`text-lg font-mono font-bold ${serverInfo.error ? 'text-red-500' : ''}`} style={serverInfo.error ? {} : { color: '#003594' }}>{serverInfo.public_ip}</code>
                <div className="flex items-center gap-2 mt-1 justify-end">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${serverInfo.banco_plaza_environment === 'production' ? 'bg-green-100 text-green-800' : serverInfo.banco_plaza_environment === 'test' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                    {serverInfo.banco_plaza_environment}
                  </span>
                  {serverInfo.banco_plaza_url && <span className="text-xs text-gray-400 font-mono">{serverInfo.banco_plaza_url}</span>}
                </div>
              </div>
              {serverInfo.error ? (
                <button onClick={onReload} className="px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-red-500 hover:bg-red-600">Reintentar</button>
              ) : (
                <button onClick={() => { navigator.clipboard.writeText(serverInfo.public_ip); setToast({ type: 'success', message: 'IP copiada al portapapeles' }); }} className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50" title="Copiar IP">Copiar</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exchange Rate */}
      {exchangeRate && exchangeRate.usd_ves && (
        <div data-testid="exchange-rate-panel" className="mb-6 bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Tasa de Cambio</h3>
                <p className="text-xs text-gray-500">{exchangeRate.source} - {exchangeRate.fecha}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-xs text-gray-500">USD / VES</div>
                <code className="text-xl font-mono font-bold text-green-700">Bs. {exchangeRate.usd_ves?.toFixed(2)}</code>
              </div>
              {exchangeRate.eur_ves && (
                <div className="text-right">
                  <div className="text-xs text-gray-500">EUR / VES</div>
                  <code className="text-lg font-mono font-bold text-blue-700">Bs. {exchangeRate.eur_ves?.toFixed(2)}</code>
                </div>
              )}
              <button onClick={refreshRate} className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50">Actualizar</button>
            </div>
          </div>
          {exchangeRate.cached && <p className="text-[10px] text-gray-400 mt-2">Cache activo {exchangeRate.stale ? '(datos anteriores - fuente no disponible)' : ''}</p>}
        </div>
      )}

      <ErrorBoundary fallbackTitle="Error mostrando saldos de cuentas bancarias">
        <BankAccountsBalance />
      </ErrorBoundary>

      {/* Email Onboarding Switch */}
      <div className="mb-6 bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5" style={{ color: '#003594' }} />
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Email de Onboarding</h3>
              <p className="text-xs text-gray-500">Enviar email automatico al crear comercio</p>
            </div>
          </div>
          <button
            data-testid="toggle-onboarding-email"
            onClick={toggleEmail}
            disabled={togglingEmail}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${emailEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${emailEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      <div className="mt-6"><BankCommissionsTable setToast={setToast} /></div>
      <PendingDebitsMonitor />
      <ErrorBoundary fallbackTitle="Error mostrando movimientos bancarios">
        <BankAccountMovements />
      </ErrorBoundary>
      <div className="mt-6">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#051C2C' }}>Analytics y Tendencias</h2>
        <AdvancedAnalytics />
      </div>
    </div>
  );
}
